/**
 * Wordle Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum WordleErrorType {
  INVALID_WORD = 'INVALID_WORD',
  NOT_ENOUGH_LETTERS = 'NOT_ENOUGH_LETTERS',
  HARD_MODE_VIOLATION = 'HARD_MODE_VIOLATION',
  DAILY_ALREADY_COMPLETED = 'DAILY_ALREADY_COMPLETED',
  GAME_LOGIC = 'GAME_LOGIC',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface WordleError {
  type: WordleErrorType;
  message: string;
  details?: unknown;
  timestamp: number;
  recoverable: boolean;
}

export class WordleErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: WordleError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: WordleErrorType,
    message: string,
    details?: unknown,
    showNotification: boolean = true
  ): WordleError {
    const error: WordleError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    // Log to error service
    this.errorService.handleError(
      new Error(message),
      this.mapToErrorType(type),
      this.mapToSeverity(type)
    );

    // Show notification for user-facing errors
    if (showNotification) {
      this.notificationService.addNotification({
        type: this.mapToNotificationType(type),
        title: this.getErrorTitle(type),
        message,
        duration: 3000,
      });
    }

    // Track in local log
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }

    return error;
  }

  getErrorLog(): WordleError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }

  private isRecoverable(type: WordleErrorType): boolean {
    switch (type) {
      case WordleErrorType.INVALID_WORD:
      case WordleErrorType.NOT_ENOUGH_LETTERS:
      case WordleErrorType.HARD_MODE_VIOLATION:
        return true;
      case WordleErrorType.DAILY_ALREADY_COMPLETED:
      case WordleErrorType.SAVE_ERROR:
      case WordleErrorType.LOAD_ERROR:
      case WordleErrorType.NETWORK_ERROR:
        return false;
      default:
        return false;
    }
  }

  private mapToErrorType(type: WordleErrorType): ErrorType {
    switch (type) {
      case WordleErrorType.INVALID_WORD:
      case WordleErrorType.NOT_ENOUGH_LETTERS:
      case WordleErrorType.HARD_MODE_VIOLATION:
      case WordleErrorType.GAME_LOGIC:
        return ErrorType.GAME_LOGIC;
      case WordleErrorType.SAVE_ERROR:
      case WordleErrorType.LOAD_ERROR:
        return ErrorType.STORAGE;
      case WordleErrorType.NETWORK_ERROR:
        return ErrorType.NETWORK;
      default:
        return ErrorType.SYSTEM;
    }
  }

  private mapToSeverity(type: WordleErrorType): ErrorSeverity {
    switch (type) {
      case WordleErrorType.INVALID_WORD:
      case WordleErrorType.NOT_ENOUGH_LETTERS:
      case WordleErrorType.HARD_MODE_VIOLATION:
      case WordleErrorType.DAILY_ALREADY_COMPLETED:
        return ErrorSeverity.LOW;
      case WordleErrorType.GAME_LOGIC:
      case WordleErrorType.SAVE_ERROR:
      case WordleErrorType.LOAD_ERROR:
        return ErrorSeverity.MEDIUM;
      case WordleErrorType.NETWORK_ERROR:
      case WordleErrorType.UNKNOWN:
        return ErrorSeverity.HIGH;
      default:
        return ErrorSeverity.MEDIUM;
    }
  }

  private mapToNotificationType(type: WordleErrorType): NotificationType {
    switch (type) {
      case WordleErrorType.INVALID_WORD:
      case WordleErrorType.NOT_ENOUGH_LETTERS:
      case WordleErrorType.HARD_MODE_VIOLATION:
        return NotificationType.WARNING;
      case WordleErrorType.DAILY_ALREADY_COMPLETED:
        return NotificationType.INFO;
      default:
        return NotificationType.ERROR;
    }
  }

  private getErrorTitle(type: WordleErrorType): string {
    switch (type) {
      case WordleErrorType.INVALID_WORD:
        return 'Invalid Word';
      case WordleErrorType.NOT_ENOUGH_LETTERS:
        return 'Too Short';
      case WordleErrorType.HARD_MODE_VIOLATION:
        return 'Hard Mode';
      case WordleErrorType.DAILY_ALREADY_COMPLETED:
        return 'Already Completed';
      case WordleErrorType.SAVE_ERROR:
        return 'Save Failed';
      case WordleErrorType.LOAD_ERROR:
        return 'Load Failed';
      case WordleErrorType.NETWORK_ERROR:
        return 'Connection Error';
      default:
        return 'Error';
    }
  }
}
